import React from "react";
import { useSelector } from "react-redux";

import { CoinIcon } from "../reward/RewardIcons";
import { formatPoint } from "../reward/pointStore";
import HomeCover from "./HomeCover";
import useHomeData from "./useHomeData";
import useHomeActions from "./useHomeActions";
import useHomeSubject from "./useHomeSubject";
import { noteTextOf, reviewLinkTextOf } from "./homeStatus";
import { KidTopCards, LevelBandCard, SubjectTabs, TodayCard, WeakCard, WordTrendCard } from "./StatusBlocks";
import { FINISHED_TAB, NOTICE_TAB, WRONG_NOTE_TAB } from "./homeTabs";
import {
  academyThemeStyleOf,
  actionTextOf,
  ddayOf,
  finishedDateOf,
  formatDueText,
  formatShortDate,
  formatTodayText,
  kindLabelOf,
  LOW_SCORE_RATE,
  retryTextOf,
  scoreOf,
} from "./homeUtils";
import "./Home.css";
import "./SecondaryHome.css";

const RECENT_SCORE_COUNT = 4;

// 중고등 홈(2026-10-26 시안 kinder-home-secondary). 상단 바는 지금의 TopNavBar 그대로다.
//  - 옆칸(폰에서는 인사 바로 아래): 레벨(리포트) · 최근 점수(틀린 문제) · 알림장. 엄마가 먼저 보는 칸이다
//  - 본문: 이어서 하기 한 장 + 해야 할 과제 카드
// 시험 D-day 띠 · 오늘 목표 · 이번 주 출석 · 인기 기프티콘은 데이터가 없거나 대표가 뺀 것이라 두지 않았다.
const SecondaryHome = () => {
  const { student } = useSelector((state) => state.student);
  const isMathStudent = student?.site === "math";
  const homeData = useHomeData(student?.id);
  const { noteList, levelTest, summary, reloadTodo } = homeData;
  const { subject, setSubject, hasBoth, isEnglish, isMath, todoList, finishedList } = useHomeSubject({
    todoList: homeData.todoList,
    finishedList: homeData.finishedList,
    site: student?.site,
  });
  const { openTodo, openFinished, openById, goTab, paperModal } = useHomeActions({
    onPaperModalClose: reloadTodo,
    todoList: homeData.todoList,
  });

  const itemList = todoList || [];
  // 이어서 하기: 하다 만 것 중 마감이 가장 가까운 것. 없으면 맨 앞 과제
  const resumeItem =
    itemList.find((item) => item.type !== "paper_assignment" && !item.isLevelTestReview && Number(item.progress_rate) > 0) ||
    itemList[0] ||
    null;
  const restList = itemList.filter((item) => item !== resumeItem);
  const overdueCount = itemList.filter((item) => ddayOf(item.closed_at)?.tone === "over").length;

  const recentList = finishedList.filter((item) => scoreOf(item) !== null).slice(0, RECENT_SCORE_COUNT);

  const resumeDday = resumeItem ? ddayOf(resumeItem.closed_at) : null;
  const resumeProgress = resumeItem ? Number(resumeItem.progress_rate) || 0 : 0;
  const resumeReward = resumeItem ? Number(resumeItem.reward_points) || 0 : 0;
  const resumeRewardMin = resumeItem ? Number(resumeItem.reward_min_score) || 0 : 0;

  return (
    <div className="student-home secondary-home" style={academyThemeStyleOf(student)}>
      <div className="secondary-home__page">
        {hasBoth && (
          <div className="secondary-home__subject">
            <SubjectTabs subject={subject} onChange={setSubject} />
          </div>
        )}

        <header className="secondary-home__hello">
          <div>
            <div className="secondary-home__date">{formatTodayText()}</div>
            <h1 className="secondary-home__title">{student?.name ? `${student.name}의 오늘 학습` : "오늘 학습"}</h1>
          </div>
          {todoList !== null && (
            <div className="secondary-home__stats">
              <div className="secondary-home__stat">
                <b>{itemList.length}</b>
                <span>남은 과제</span>
              </div>
              {overdueCount > 0 && (
                <div className="secondary-home__stat">
                  <b className="home-score--low">{overdueCount}</b>
                  <span>기한 지남</span>
                </div>
              )}
            </div>
          )}
        </header>

        <div className="secondary-home__today">
          <TodayCard
            studentName={student?.name}
            today={summary?.today}
            wordTest={isEnglish ? summary?.word_test : null}
            onOpenWordTest={() => openById(summary.word_test.assignment_id, "vocabulary")}
          />
        </div>

        <div className="secondary-home__side">
          {isEnglish && <LevelBandCard studentName={student?.name} levelTest={levelTest} />}
        <aside className="secondary-side">
          <section className="secondary-side__block">
            <div className="secondary-side__head">
              <h3>지난 기록</h3>
              <button type="button" className="home-link" onClick={() => goTab(FINISHED_TAB.name)}>
                전체 ›
              </button>
            </div>
            {recentList.length > 0 ? (
              <ul className="secondary-scores">
                {recentList.map((item) => {
                  const score = scoreOf(item);
                  return (
                    <li key={item.id}>
                      <button type="button" className="secondary-score" onClick={() => openFinished(item)}>
                        <span className="secondary-score__text">
                          <span className="secondary-score__title">{item.title}</span>
                          <span className="secondary-score__meta">
                            {formatShortDate(finishedDateOf(item))} · {kindLabelOf(item)} · <span className="secondary-score__go">{reviewLinkTextOf(item)} ›</span>
                          </span>
                        </span>
                        <b className={`secondary-score__value${score.rate < LOW_SCORE_RATE ? " home-score--low" : ""}`}>
                          {score.text.replace(/점$/, "")}
                        </b>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="secondary-side__empty">아직 끝낸 과제가 없어요.</p>
            )}
            {(isMathStudent || isMath) && (
              <button type="button" className="home-go home-go--ghost secondary-side__wide" onClick={() => goTab(WRONG_NOTE_TAB.name)}>
                오답노트
              </button>
            )}
          </section>

          <section className="secondary-side__block">
            <div className="secondary-side__head">
              <h3>알림장</h3>
              <button type="button" className="home-link" onClick={() => goTab(NOTICE_TAB.name)}>
                전체 ›
              </button>
            </div>
            {noteList.length > 0 ? (
              <ul className="secondary-notes">
                {noteList.map((note) => (
                  <li key={note.id}>
                    <button type="button" className="secondary-note" onClick={() => goTab(NOTICE_TAB.name)}>
                      <span className="secondary-note__date">{formatShortDate(note.class_date || note.created_at)}</span>
                      <span className="secondary-note__text">{noteTextOf(note)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="secondary-side__empty">아직 알림장이 없어요.</p>
            )}
          </section>
        </aside>
        </div>

        {isEnglish && (summary?.weak_list?.length > 0 || summary?.word_trend?.length > 1) && (
          <div className="secondary-home__status">
            <WeakCard weakList={summary?.weak_list} onOpenRetry={(retry) => openById(retry.assignment_id, "", retry.is_finished)} />
            <WordTrendCard trendList={summary?.word_trend} />
          </div>
        )}

        <div className="secondary-home__main">
          <KidTopCards
            studentName={student?.name}
            photoBonus={isMath ? summary?.photo_bonus : null}
            onOpenPhotoBonus={() => openById(summary.photo_bonus.assignment_id)}
          />

          {todoList !== null && itemList.length === 0 && <div className="home-empty">미완료 과제가 없어요. 전부 끝냈습니다!</div>}

          {resumeItem && (
            <section className="secondary-resume">
              <div className="secondary-resume__paper" onClick={() => openTodo(resumeItem)}>
                <HomeCover item={resumeItem} />
              </div>
              <div className="secondary-resume__body">
                <div className="secondary-resume__kicker">
                  {resumeProgress > 0 ? "이어서 하기" : "먼저 할 과제"} · {kindLabelOf(resumeItem)}
                  {resumeItem.is_retry && ` · ${retryTextOf(resumeItem)}`}
                  {resumeDday && (
                    <span className={resumeDday.tone !== "calm" ? "is-hot" : ""}> · {resumeDday.text}</span>
                  )}
                </div>
                <h2 className="secondary-resume__title">{resumeItem.title}</h2>
                {resumeItem.type !== "paper_assignment" && !resumeItem.isLevelTestReview && (
                  <div className="home-progress secondary-resume__progress">
                    <span className="home-progress__bar">
                      <i style={{ width: `${resumeProgress}%` }} />
                    </span>
                    {resumeProgress}%
                  </div>
                )}
                <div className="secondary-resume__foot">
                  <span className="secondary-resume__due">
                    {resumeItem.closed_at && `마감 ${formatDueText(resumeItem.closed_at)}`}
                  </span>
                  {resumeReward > 0 && (
                    <span className="home-reward">
                      <CoinIcon size="1em" />
                      {resumeRewardMin > 0 ? `${resumeRewardMin}점 이상이면 +${formatPoint(resumeReward)}` : `+${formatPoint(resumeReward)}`}
                    </span>
                  )}
                  <button type="button" className="home-go" onClick={() => openTodo(resumeItem)}>
                    {actionTextOf(resumeItem)}
                  </button>
                </div>
              </div>
            </section>
          )}

          {restList.length > 0 && (
            <section>
              <div className="secondary-home__section-head">
                <h2>해야 할 과제</h2>
                <small>{restList.length}</small>
              </div>
              <div className="secondary-lessons">
                {restList.map((item) => {
                  const dday = ddayOf(item.closed_at);
                  const progress = Number(item.progress_rate) || 0;
                  const reward = Number(item.reward_points) || 0;
                  const showProgress = item.type !== "paper_assignment" && !item.isLevelTestReview;
                  return (
                    <article key={item.id} className="secondary-lesson" onClick={() => openTodo(item)}>
                      <HomeCover
                        item={item}
                        badge={dday ? dday.text : ""}
                        badgeTone={dday ? dday.tone : "calm"}
                      />
                      <div className="secondary-lesson__body">
                        <span className="secondary-lesson__kind">
                          {kindLabelOf(item)}
                          {item.is_retry && ` · ${retryTextOf(item)}`}
                        </span>
                        <span className="secondary-lesson__title">{item.title}</span>
                        {showProgress && (
                          <div className="home-progress secondary-lesson__progress">
                            <span className="home-progress__bar">
                              <i style={{ width: `${progress}%` }} />
                            </span>
                            {progress}%
                          </div>
                        )}
                        <div className="secondary-lesson__foot">
                          <span>
                            {item.closed_at ? `마감 ${formatDueText(item.closed_at)}` : ""}
                            {reward > 0 && (
                              <span className="secondary-lesson__reward">
                                <CoinIcon size="1em" />+{formatPoint(reward)}
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            className={`home-go home-go--small${progress > 0 ? "" : " home-go--ghost"}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openTodo(item);
                            }}
                          >
                            {actionTextOf(item)}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>

      {paperModal}
    </div>
  );
};

export default SecondaryHome;
